class CommunityBuildDetail extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;

    this.initialized = true;
    this.slides = [...this.querySelectorAll('[data-community-build-slide]')];
    this.thumbnails = [...this.querySelectorAll('[data-community-build-thumb]')];
    this.description = this.querySelector('[data-community-build-description]');
    this.descriptionContent = this.querySelector('[data-community-build-description-content]');
    this.descriptionToggle = this.querySelector('[data-community-build-description-toggle]');
    this.gallery = this.querySelector('[data-community-build-gallery]');
    this.info = this.querySelector('.community-build-detail__info');
    this.abortController = new AbortController();

    this.thumbnails.forEach((thumbnail) => {
      thumbnail.addEventListener(
        'click',
        () => this.showImage(Number.parseInt(thumbnail.dataset.communityBuildThumb, 10)),
        { signal: this.abortController.signal },
      );
      thumbnail.addEventListener(
        'keydown',
        (event) => this.handleThumbnailKeydown(event, thumbnail),
        { signal: this.abortController.signal },
      );
    });

    this.querySelector('[data-community-build-share]')?.addEventListener(
      'click',
      () => this.share(),
      { signal: this.abortController.signal },
    );

    this.descriptionToggle?.addEventListener(
      'click',
      () => this.toggleDescription(),
      { signal: this.abortController.signal },
    );

    window.addEventListener('resize', () => this.scheduleDescriptionClamp(), {
      signal: this.abortController.signal,
    });

    if (this.gallery && 'ResizeObserver' in window) {
      this.descriptionResizeObserver = new ResizeObserver(() => this.scheduleDescriptionClamp());
      this.descriptionResizeObserver.observe(this.gallery);
    }

    this.scheduleDescriptionClamp();
    document.fonts?.ready.then(() => {
      if (this.isConnected) this.scheduleDescriptionClamp();
    });
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.descriptionResizeObserver?.disconnect();
    window.cancelAnimationFrame(this.descriptionClampFrame);
    this.initialized = false;
  }

  scheduleDescriptionClamp() {
    window.cancelAnimationFrame(this.descriptionClampFrame);
    this.descriptionClampFrame = window.requestAnimationFrame(() => this.updateDescriptionClamp());
  }

  resetDescriptionClamp() {
    if (!this.description || !this.descriptionContent || !this.descriptionToggle) return;

    this.description.removeAttribute('data-description-clamped');
    this.descriptionContent.style.removeProperty('max-height');
    this.descriptionToggle.hidden = true;
  }

  updateDescriptionClamp() {
    if (!this.description || !this.descriptionContent || !this.descriptionToggle) return;

    this.resetDescriptionClamp();

    if (this.description.dataset.descriptionExpanded === 'true') {
      this.descriptionToggle.hidden = false;
      this.descriptionToggle.textContent = this.descriptionToggle.dataset.collapseLabel || 'Show less';
      this.descriptionToggle.setAttribute('aria-expanded', 'true');
      this.descriptionToggle.setAttribute(
        'aria-label',
        this.descriptionToggle.dataset.collapseLabel || 'Show less',
      );
      return;
    }

    if (!window.matchMedia('(min-width: 981px)').matches || !this.gallery || !this.info) {
      return;
    }

    const galleryHeight = this.gallery.getBoundingClientRect().height;
    const infoTop = this.info.getBoundingClientRect().top;
    const infoBottom = [...this.info.children].reduce((bottom, child) => {
      if (getComputedStyle(child).display === 'none') return bottom;
      return Math.max(bottom, child.getBoundingClientRect().bottom);
    }, infoTop);
    const infoHeight = infoBottom - infoTop;
    const contentHeight = this.descriptionContent.scrollHeight;
    const overflow = infoHeight - galleryHeight;

    if (overflow <= 1) return;

    const lineHeight = Number.parseFloat(getComputedStyle(this.description).lineHeight);
    const availableHeight = contentHeight - overflow;
    const visibleLines = Math.max(1, Math.floor(availableHeight / lineHeight));
    const maxHeight = Math.min(contentHeight - 1, visibleLines * lineHeight);

    if (maxHeight <= 0 || maxHeight >= contentHeight - 1) return;

    this.descriptionContent.style.maxHeight = `${maxHeight}px`;
    this.description.dataset.descriptionClamped = 'true';
    this.descriptionToggle.hidden = false;
    this.descriptionToggle.textContent = '…';
    this.descriptionToggle.setAttribute('aria-expanded', 'false');
    this.descriptionToggle.setAttribute(
      'aria-label',
      this.descriptionToggle.dataset.expandLabel || 'Show full description',
    );
  }

  toggleDescription() {
    if (!this.description || !this.descriptionContent || !this.descriptionToggle) return;

    const expanded = this.description.dataset.descriptionExpanded === 'true';

    if (expanded) {
      delete this.description.dataset.descriptionExpanded;
      this.updateDescriptionClamp();
      return;
    }

    this.description.dataset.descriptionExpanded = 'true';
    this.description.removeAttribute('data-description-clamped');
    this.descriptionContent.style.removeProperty('max-height');
    this.descriptionToggle.hidden = false;
    this.descriptionToggle.textContent = this.descriptionToggle.dataset.collapseLabel || 'Show less';
    this.descriptionToggle.setAttribute('aria-expanded', 'true');
    this.descriptionToggle.setAttribute(
      'aria-label',
      this.descriptionToggle.dataset.collapseLabel || 'Show less',
    );
  }

  showImage(index) {
    if (!Number.isInteger(index) || !this.slides[index]) return;

    this.slides.forEach((slide, slideIndex) => {
      slide.hidden = slideIndex !== index;
    });

    this.thumbnails.forEach((thumbnail, thumbnailIndex) => {
      thumbnail.setAttribute('aria-selected', String(thumbnailIndex === index));
      thumbnail.tabIndex = thumbnailIndex === index ? 0 : -1;
    });
  }

  handleThumbnailKeydown(event, thumbnail) {
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;

    event.preventDefault();
    const currentIndex = this.thumbnails.indexOf(thumbnail);
    let targetIndex = currentIndex;

    if (event.key === 'ArrowLeft') {
      targetIndex = (currentIndex - 1 + this.thumbnails.length) % this.thumbnails.length;
    } else if (event.key === 'ArrowRight') {
      targetIndex = (currentIndex + 1) % this.thumbnails.length;
    } else if (event.key === 'Home') {
      targetIndex = 0;
    } else if (event.key === 'End') {
      targetIndex = this.thumbnails.length - 1;
    }

    this.showImage(targetIndex);
    this.thumbnails[targetIndex]?.focus();
  }

  async share() {
    const button = this.querySelector('[data-community-build-share]');
    const label = button?.querySelector('[data-community-build-share-label]');
    const url = this.dataset.buildUrl || window.location.href;
    const title = this.dataset.buildTitle || document.title;

    if (!button || !label) return;

    try {
      if (navigator.share) {
        await navigator.share({ title, url });
        return;
      }

      await navigator.clipboard.writeText(url);
      label.textContent = button.dataset.shareSuccess || 'Link copied';
      window.setTimeout(() => {
        label.textContent = button.dataset.shareLabel || 'Share this build';
      }, 2500);
    } catch (error) {
      if (error?.name !== 'AbortError') {
        window.location.href = `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(url)}`;
      }
    }
  }
}

if (!customElements.get('community-build-detail')) {
  customElements.define('community-build-detail', CommunityBuildDetail);
}
